import React, { useMemo, useState } from "react";
import {
  Box, Button, Chip, IconButton, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, alpha, FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SystemUpdateIcon from "@mui/icons-material/SystemUpdate";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import {
  useGetAppReleasesQuery, useUpdateAppReleaseMutation, useDeleteAppReleaseMutation,
} from "../../Api/attoDeskApi";
import { IAppRelease } from "../../Api/Interface/api.interface";
import { useAppSelector } from "../../Store/hooks";
import { selectIsSuperAdmin } from "../../Store/Auth/AuthSelector";
import { useNotifier } from "../../Core/Notifier";
import PageLoader from "../../Components/Loader/PageLoader";
import DeletePopup from "../../Components/Delete/DeletePopup";
import {
  NAVY, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../theme/tokens";
import UploadWizard from "./UploadWizard";
import { formatBytes, CHANNEL_META } from "./updateShared";

const AppUpdates: React.FC = () => {
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, refetch } = useGetAppReleasesQuery(
    { include_inactive: includeInactive }, { skip: !isSuper },
  );
  const [update] = useUpdateAppReleaseMutation();
  const [deleteRelease] = useDeleteAppReleaseMutation();

  const rows: IAppRelease[] = useMemo(
    () => (Array.isArray(data?.data) ? (data?.data as unknown as IAppRelease[]) : []),
    [data],
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState<IAppRelease | null>(null);

  const toggleActive = async (r: IAppRelease) => {
    try {
      const res = await update({ id: r.id, is_active: !r.is_active }).unwrap();
      if (res.status) { showMessage(r.is_active ? "Deactivated" : "Activated"); refetch(); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteRelease(toDelete.id).unwrap();
      if (res.status) { showMessage("Release deleted"); setToDelete(null); refetch(); }
      else showErrorMessage(res.message || "Failed");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          App release management is visible to Super Admins only.
        </Typography>
      </Box>
    );
  }
  if (isLoading) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#3b82f6", 0.18)} 0%, ${alpha("#3b82f6", 0.06)} 100%)`,
          color: "#3b82f6",
        }}>
          <SystemUpdateIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">App Updates</Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Upload APKs, target companies / groups / devices, and manage staged rollouts. Devices poll periodically and immediately on the "install_update" command.
          </Typography>
        </Box>
        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setUploadOpen(true)}
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700,
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`, color: "#0b1e2d" }}>
          Upload Release
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, alignItems: "center" }}>
          <FormControlLabel
            control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12, fontWeight: 600 }}>Include inactive releases</Typography>} />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["Version", "Platform", "Channel", "Size", "Checksum", "Release Notes", "Active", "Uploaded", ""].map((h, i) => (
                  <TableCell key={h || String(i)} align={i === 8 ? "right" : "left"}
                    sx={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 6, color: TEXT_SECONDARY }}>
                    No releases yet. Upload an APK to get started.
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => {
                const cc = CHANNEL_META[r.release_channel] || CHANNEL_META.stable;
                return (
                  <TableRow key={r.id} hover sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/app-updates/${r.id}`)}>
                    <TableCell>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{r.version_name}</Typography>
                      <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace" }}>code: {r.version_code}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{r.platform.toUpperCase()}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.release_channel.toUpperCase()}
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: cc.bg, color: cc.fg }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>{formatBytes(r.file_size)}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: TEXT_SECONDARY, fontFamily: "monospace", maxWidth: 160 }}>
                      <Tooltip title={r.checksum || ""}><span>{r.checksum ? r.checksum.slice(0, 10) + "…" : "—"}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, maxWidth: 240 }}>
                      <Typography noWrap sx={{ fontSize: 12 }}>{r.release_notes || "—"}</Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch size="small" checked={r.is_active} onChange={() => toggleActive(r)} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: TEXT_SECONDARY, whiteSpace: "nowrap" }}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Release details">
                        <IconButton size="small" onClick={() => navigate(`/app-updates/${r.id}`)}
                          sx={{ color: "#64748b", "&:hover": { color: NAVY, bgcolor: "#f1f5f9" } }}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setToDelete(r)}
                          sx={{ color: "#94a3b8", "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" } }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <UploadWizard open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={refetch} />

      <DeletePopup
        open={!!toDelete}
        handleCloseDelete={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Release"
        content={toDelete
          ? `Delete release ${toDelete.version_name} (code ${toDelete.version_code})? Releases still installed on or assigned to devices can't be deleted — deactivate instead.`
          : ""}
      />
    </Box>
  );
};

export default AppUpdates;
